import * as React from 'react'

import { Box, Grid, Spinner, Title } from '@factorialco/deprecated-design-system'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import {
  DocumentsUploadProcessForStatusItemFragment,
  DocumentsUploadProcessesForStatusDocument,
  ProcessedDocumentsCountSubscriptionDocument,
  DocumentsUploadProcessStatusesEnum as UploadProcessStatuses,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useCallback } from 'modules/core/hooks/useCallback'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { useMemo, useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useSubscription } from 'modules/core/hooks/useSubscription'
import { companyCurrentIsoCountry } from 'modules/core/lib/companyCurrentIsoCountry'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'
import companies from 'modules/core/store/collections/companies'

import { companyInExpiredFreeTrial } from 'modules/billing/store/collections/subscriptions'
import CancelUploadProcess from 'modules/documents/components/BulkFiles/CancelUploadProcess'
import DocumentAssigner from 'modules/documents/components/BulkFiles/DocumentAssigner'
import DocumentTemplateStepsModal from 'modules/documents/components/BulkFiles/DocumentTemplateStepsModal'
import PersonalFilesStepsModal from 'modules/documents/components/BulkFiles/PersonalFilesStepsModal'
import SameFileEveryoneStepsModal from 'modules/documents/components/BulkFiles/SameFileEveryoneStepsModal'
import SendInBulkModeCard from 'modules/documents/components/BulkFiles/SendInBulkModeCard'
import UploadProcessCard from 'modules/documents/components/UploadProcessCard'
import useGetDocuments from 'modules/documents/hooks/documents/useGetDocuments'
import useFilesStore from 'modules/documents/hooks/files/useFilesStore'
import { useGetFolders } from 'modules/documents/hooks/useGetFolders'
import { useGetUploadProcess } from 'modules/documents/hooks/useGetUploadProcess'
import { isWritableFolder } from 'modules/documents/lib/Folders'
import { DISTRIBUTION_WORKFLOWS, SPACES } from 'modules/documents/lib/constants'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { RequestUpgradeProps } from 'modules/upsell/lib/requestUpgradeTypes'
import { UpsellingKit } from 'modules/upselling_kit/components'

import ManualDocumentAssigner from './ManualDocumentAssigner'
import PayslipsStepsModal from './PayslipsStepsModal'

const DEFAULT_POLLING_TIME = 2000
const LONG_POLLING_TIME = 5 * 60 * 1000

interface Props {
  canManageDocumentTemplates: boolean
}

const BulkFiles = ({ canManageDocumentTemplates }: Props) => {
  const { loading: loadingFeatureAvailability, featureAvailability } = useFeatureAvailability(
    Features.documents_templates
  )
  const {
    uploadProcess: currentUploadProcess,
    loading: loadingUploadProcess,
    refetch: refetchUploadProcess,
  } = useGetUploadProcess()

  const { data: uploadProcessForStatusData, refetch } = useQuery(
    DocumentsUploadProcessesForStatusDocument
  )
  const {
    documents: documents,
    loading: loadingDocuments,
    refetch: fetchDocuments,
  } = useGetDocuments({
    byPendingAssignment: true,
  })
  const [processedDocuments, setProcessedDocuments] = React.useState<number | undefined>(0)
  const [expectedDocuments, setExpectedDocuments] = React.useState<number | undefined>(0)
  const [processedPages, setProcessedPages] = React.useState<number | undefined>(0)
  const [expectedPages, setExpectedPages] = React.useState<number | undefined>(0)
  const [uploadProcessForStatus, setUploadProcessForStatus] = React.useState<
    undefined | DocumentsUploadProcessForStatusItemFragment
  >()

  const { data: processedDocumentsCountData } = useSubscription(
    ProcessedDocumentsCountSubscriptionDocument
  )

  useUnsafeEffect(() => {
    const results = uploadProcessForStatusData?.documents.uploadProcessesConnection.nodes || []
    setUploadProcessForStatus(results[results.length - 1])
  }, [uploadProcessForStatusData])

  useUnsafeEffect(() => {
    const uploadProcessFromSubscription =
      processedDocumentsCountData?.documentsUploadProcessProcessedDocumentsCount?.uploadProcess

    if (uploadProcessFromSubscription?.id === uploadProcessForStatus?.id) {
      setExpectedDocuments(uploadProcessFromSubscription?.totalDocumentsCount)
      setProcessedDocuments(uploadProcessFromSubscription?.processedDocumentsCount)

      if (uploadProcessForStatus?.uploadType === 'manual_per_page') {
        setExpectedPages(uploadProcessFromSubscription?.totalPagesCount)
        setProcessedPages(uploadProcessFromSubscription?.processedPagesCount)
      }
    }
  }, [processedDocumentsCountData])

  useUnsafeEffect(() => {
    if (
      uploadProcessForStatus?.currentDocumentsCount &&
      uploadProcessForStatus?.currentDocumentsCount > (processedDocuments ?? 0)
    )
      setProcessedDocuments(uploadProcessForStatus?.currentDocumentsCount || 0)

    if (
      uploadProcessForStatus?.expectedDocumentsCount &&
      uploadProcessForStatus?.expectedDocumentsCount > (expectedDocuments ?? 0)
    )
      setExpectedDocuments(uploadProcessForStatus?.expectedDocumentsCount || 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    uploadProcessForStatus?.currentDocumentsCount,
    uploadProcessForStatus?.expectedDocumentsCount,
  ])

  useUnsafeEffect(() => {
    refetchUploadProcess()
    setProcessedDocuments(0)
    setExpectedDocuments(0)
    setProcessedPages(0)
    setExpectedPages(0)
  }, [uploadProcessForStatus?.status])

  const { folders, loading: loadingFolders } = useGetFolders({
    space: SPACES.EMPLOYEE_MY_DOCUMENTS,
  })

  const isoCountryCode = companyCurrentIsoCountry(companies.current)
  const isReadOnly = companyInExpiredFreeTrial(companies.current)
  const [initialLoading, setInitialLoading] = React.useState(true)
  const [isUpsellModalOpen, setIsUpsellModalOpen] = React.useState(false)
  const [showCancelModal, setShowCancelModal] = React.useState(false)
  const [waitingForUploadProcess, setWaitingForUploadProcess] = React.useState(false)
  const uploadProcess = useUnsafeMemo(() => currentUploadProcess, [currentUploadProcess])
  const [currentWorkflow, setCurrentWorkflow] = React.useState<DISTRIBUTION_WORKFLOWS | undefined>()
  const hasTemplateFeature = featureAvailability.available
  const showEnterpriseTag = !hasTemplateFeature
  const { addFiles, onDeleteFile, deleteAllFiles } = useFilesStore()

  useEffect(() => {
    if (initialLoading && !loadingUploadProcess) {
      setInitialLoading(false)
    }
  }, [loadingUploadProcess, initialLoading])

  const refetchAndWait = async () => {
    setWaitingForUploadProcess(true)

    fetchDocuments()

    await refetch()

    setWaitingForUploadProcess(false)
  }

  const intervalId = React.useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    refetch()
    fetchDocuments()
  }, [])

  const calculatePollingTime = () => {
    if (uploadProcess?.status === 'ready' && payslipsWorkflow) {
      return LONG_POLLING_TIME
    } else {
      return DEFAULT_POLLING_TIME
    }
  }

  const startPolling = useCallback(async () => {
    const polling = calculatePollingTime()

    intervalId.current = setInterval(async () => {
      await refetch()
    }, polling)
  }, [uploadProcess?.status])

  const stopPolling = () => {
    const currentId = intervalId.current
    if (currentId) clearTimeout(currentId)
  }

  useEffect(() => {
    startPolling()

    return () => {
      stopPolling()
    }
  }, [])

  useEffect(() => {
    stopPolling()
    startPolling()
  }, [uploadProcess?.status])

  useEffect(() => {
    setShowCancelModal(false)

    if (uploadProcess?.id && !documents.length) {
      refetchAndWait()
    }
  }, [uploadProcess?.id, documents.length])

  useEffect(() => {
    if (uploadProcess?.status === 'ready') {
      fetchDocuments()
    }
  }, [uploadProcess?.status])

  const writableFolders = useMemo(
    () => folders.filter((folder) => isWritableFolder(folder)),
    [folders.map((folder) => folder.id).join(',')]
  )

  const onUploadFinished = () => {
    deleteAllFiles()
    setCurrentWorkflow(undefined)
    return refetchAndWait()
  }

  const onCloseUploader = () => {
    deleteAllFiles()
    setCurrentWorkflow(undefined)
  }

  const onDropFiles = (files: File[]) => {
    addFiles(files)
  }

  const onCancel = async () => {
    await refetch()
  }

  const onTemplate = () => {
    if (!hasTemplateFeature) {
      setIsUpsellModalOpen(true)
      return
    }

    if (isActiveUploadProcess) {
      setShowCancelModal(true)
      return
    }

    setCurrentWorkflow('templates')
  }

  const onShareSameFiles = () => {
    if (isActiveUploadProcess) {
      setShowCancelModal(true)
      return
    }

    setCurrentWorkflow('same_files')
  }

  const onPersonalFiles = () => {
    if (isActiveUploadProcess) {
      setShowCancelModal(true)
      return
    }

    setCurrentWorkflow('personal_files')
  }

  const onPayslips = () => {
    if (isActiveUploadProcess) {
      setShowCancelModal(true)
      return
    }

    setCurrentWorkflow('payslips')
  }

  const processReady = uploadProcess?.status === 'ready'
  const noDocuments = !documents.length
  const visibleStatuses: UploadProcessStatuses[] = ['in_progress', 'error']
  const visibleStatus = !!uploadProcess?.status && visibleStatuses.includes(uploadProcess?.status)
  const payslipsWorkflow = uploadProcess?.distributionWorkflow == 'payslips'
  const showEmptyState = !uploadProcess || (processReady && noDocuments && !payslipsWorkflow)
  const showDocuments = !noDocuments && processReady
  const showPayslips = payslipsWorkflow && processReady
  const isActiveUploadProcess = uploadProcess?.active
  const loading =
    loadingDocuments ||
    loadingFolders ||
    initialLoading ||
    waitingForUploadProcess ||
    loadingFeatureAvailability

  const featureToRequest: RequestUpgradeProps = {
    moduleRequested: AvailableModules.documents,
    featureRequested: Features.documents_templates,
    requestSource: 'Documents templates',
    requestReason: ValidReasons.MissingFeature,
  }

  const workflowPayslipsTitle = i18n.t('split_documents.summary.payslips.title_v2')
  const workflowPayslipsDescription = i18n.t('split_documents.summary.payslips.description_v2')
  const workflowPersonalFilesTitle = payslipsWorkflow
    ? i18n.t('split_documents.summary.personal_files.title_v2')
    : i18n.t('split_documents.summary.personal_files.title')
  const workflowPersonalFilesDescription = payslipsWorkflow
    ? i18n.t('split_documents.summary.personal_files.description_v2')
    : i18n.t('split_documents.summary.personal_files.description')
  const workflowSameFileToEveryoneTitle = payslipsWorkflow
    ? i18n.t('split_documents.summary.same_file_to_everyone.title_v2')
    : i18n.t('split_documents.summary.same_file_to_everyone.title')
  const workflowSameFileToEveryoneDescription = payslipsWorkflow
    ? i18n.t('split_documents.summary.same_file_to_everyone.description_v2')
    : i18n.t('split_documents.summary.same_file_to_everyone.description')
  const workflowDocumentTemplateTitle = payslipsWorkflow
    ? i18n.t('split_documents.summary.document_template.title_v2')
    : i18n.t('split_documents.summary.document_template.title')
  const workflowDocumentTemplateDescription = payslipsWorkflow
    ? i18n.t('split_documents.summary.document_template.description_v2')
    : i18n.t('split_documents.summary.document_template.description')

  if (loading)
    return (
      <Box alignItems="center">
        <Spinner />
      </Box>
    )

  return (
    <>
      <Title
        title={i18n.t('split_documents.summary.title_v2')}
        link={{
          href: helpCenterUrl('documents_in_bulk'),
          label: i18n.t('split_documents.summary.description_link_v2'),
        }}
        variant="500"
      />
      {visibleStatus && (
        <UploadProcessCard
          uploadProcess={uploadProcess}
          processedDocuments={processedDocuments}
          expectedDocuments={expectedDocuments}
          processedPages={processedPages}
          expectedPages={expectedPages}
          onCancel={refetch}
        />
      )}
      <>
        {showEmptyState && (
          <Grid variant="medium">
            <SendInBulkModeCard
              title={workflowPayslipsTitle}
              description={workflowPayslipsDescription}
              onClick={onPayslips}
            />
            <SendInBulkModeCard
              title={workflowSameFileToEveryoneTitle}
              description={workflowSameFileToEveryoneDescription}
              onClick={onShareSameFiles}
            />
            <SendInBulkModeCard
              title={workflowPersonalFilesTitle}
              description={workflowPersonalFilesDescription}
              onClick={onPersonalFiles}
            />
            <SendInBulkModeCard
              title={workflowDocumentTemplateTitle}
              description={workflowDocumentTemplateDescription}
              tag={
                showEnterpriseTag
                  ? i18n.t('split_documents.summary.document_template.enterprise')
                  : undefined
              }
              disabled={hasTemplateFeature && !canManageDocumentTemplates}
              disabledTooltip={i18n.t(
                'split_documents.summary.document_template.do_not_have_permission_description'
              )}
              onClick={onTemplate}
            />
          </Grid>
        )}
        {showDocuments && currentUploadProcess && (
          <DocumentAssigner
            documents={documents}
            folders={writableFolders}
            uploadProcess={currentUploadProcess}
            onRefresh={refetchAndWait}
          />
        )}
        {showPayslips && currentUploadProcess && (
          <ManualDocumentAssigner uploadProcess={currentUploadProcess} />
        )}
      </>
      {currentWorkflow === 'payslips' && (
        <PayslipsStepsModal
          onClose={onCloseUploader}
          onDeleteFile={onDeleteFile}
          onDropFiles={onDropFiles}
          onUploadFinished={onUploadFinished}
          isReadOnly={isReadOnly}
          folders={writableFolders}
        />
      )}
      {currentWorkflow === 'same_files' && (
        <SameFileEveryoneStepsModal
          onClose={onCloseUploader}
          onDeleteFile={onDeleteFile}
          onDropFiles={onDropFiles}
          onUploadFinished={onUploadFinished}
          isReadOnly={isReadOnly}
          folders={writableFolders}
        />
      )}
      {currentWorkflow === 'personal_files' && (
        <PersonalFilesStepsModal
          onClose={onCloseUploader}
          onDeleteFile={onDeleteFile}
          onDropFiles={onDropFiles}
          onUploadFinished={onUploadFinished}
          isReadOnly={isReadOnly}
          folders={writableFolders}
        />
      )}
      {currentWorkflow === 'templates' && (
        <DocumentTemplateStepsModal
          onClose={onCloseUploader}
          onUploadFinished={onUploadFinished}
          folders={writableFolders}
        />
      )}
      {isUpsellModalOpen && (
        <UpsellingKit.FeatureModal
          moduleRequested={featureToRequest.moduleRequested}
          requestSource={featureToRequest.requestSource ?? 'Documents templates'}
          requestReason={featureToRequest.requestReason}
          featureRequested={featureToRequest.featureRequested}
          isOpen={isUpsellModalOpen}
          onClose={() => setIsUpsellModalOpen(false)}
        />
      )}
      {showCancelModal && uploadProcess && (
        <CancelUploadProcess
          uploadProcess={uploadProcess}
          onClose={() => setShowCancelModal(false)}
          onCancel={onCancel}
          title={i18n.t('split_documents.upload_process.cancel_modal_discard.title')}
          description={i18n.t('split_documents.upload_process.cancel_modal_discard.description')}
          cancelText={i18n.t('split_documents.upload_process.cancel_modal_discard.close_modal')}
          submitText={i18n.t('split_documents.upload_process.cancel_modal_discard.submit')}
        />
      )}
    </>
  )
}

export default observer(BulkFiles)
